import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './ShopsListItem.styles';
import type { FavoriteShops } from '@/api/types/shop.types';
import { useShopFavorite } from '@/hooks/useShopFavorite';
import { cleanPhoneNumber, formatNumber, setSuffix } from '@/utils/helpers';

interface ShopListItemProps {
  shop: FavoriteShops;
  singlePhone: string | null;
  popupPhones: string[];
  onShowNumberClick: (e: React.MouseEvent<HTMLElement>) => void;
  onClosePopup: () => void;
}

export const ShopsListItem: React.FC<ShopListItemProps> = memo(
  function ShopsListItem({
    shop,
    singlePhone,
    popupPhones,
    onShowNumberClick,
    onClosePopup,
  }) {
    const { i18n, t } = useTranslation();
    const { isFavorited, toggleFavorite } = useShopFavorite({ shop });
    const logoUrl = shop.logo?.url ?? '';
    const showMultiplePhones = popupPhones.length > 0;

    return (
      <S.Card href={shop.uri} data-stat="shop-card">
        <S.TopRow>
          <S.LogoWrapper>
            <S.Logo
              src={logoUrl || undefined}
              alt={shop.name}
              width={64}
              height={64}
              loading="lazy"
              decoding="async"
            />
          </S.LogoWrapper>
          <S.Info>
            <S.Title $variant="1">{shop.name}</S.Title>
            <S.Count $variant="3">
              <S.CountLink>
                {t(
                  setSuffix(i18n, String(shop.adsCount ?? 0), 'common.n_ads'),
                  {
                    n: formatNumber(Number(shop.adsCount ?? 0), 0),
                  }
                )}
              </S.CountLink>
              {shop.timeOnPlatform != null && shop.timeOnPlatform !== '' && (
                <>
                  {' • '}
                  <S.TimeOnPlatform>{shop.timeOnPlatform}</S.TimeOnPlatform>
                </>
              )}
            </S.Count>
          </S.Info>
        </S.TopRow>
        <S.ButtonsRow>
          <S.SubscribeButton
            $isFavorited={isFavorited}
            as="button"
            type="button"
            onClick={e => {
              e.preventDefault();
              toggleFavorite();
            }}
          >
            {isFavorited
              ? t('shop.added_bookmark_shop')
              : t('shop.bookmark_shop')}
          </S.SubscribeButton>
          <S.PhonesWrap>
            {singlePhone ? (
              <S.ShowNumberButton
                as="a"
                href={`tel:${cleanPhoneNumber(singlePhone)}`}
                onClick={e => e.stopPropagation()}
              >
                {singlePhone}
              </S.ShowNumberButton>
            ) : (
              <S.ShowNumberButton
                as="button"
                type="button"
                onClick={onShowNumberClick}
              >
                {t('shop.show_number')}
              </S.ShowNumberButton>
            )}
            {showMultiplePhones && (
              <S.PhonePopup onClick={e => e.stopPropagation()}>
                {popupPhones.map((phone, i) => (
                  <S.PhoneItem
                    key={`${phone}-${i}`}
                    href={`tel:${cleanPhoneNumber(phone)}`}
                    onClick={e => {
                      e.stopPropagation();
                      onClosePopup();
                    }}
                  >
                    {phone}
                  </S.PhoneItem>
                ))}
              </S.PhonePopup>
            )}
          </S.PhonesWrap>
        </S.ButtonsRow>
      </S.Card>
    );
  }
);
